import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import AddIcon from '../../assets/baseline-add_circle-24px.svg?react';
import VertMenuIcon from '../../assets/baseline-more_vert-24px.svg?react';
import EditIcon from '../../assets/outline-edit-24px.svg?react';
import DeleteIcon from '../../assets/outline-delete-24px.svg?react';

import { backendUrl } from '../../constants';
import { Status, Box, IconBtn } from '../../components/basic';
import CompanyModal from '../../components/CompanyModal';
import ConfirmDeleteCompanyModal from '../../components/ConfirmDeleteCompanyModal';

const borderColors = ['#db536a', '#464646', '#d04a02', '#7d7d7d', '#ffb600'];
export default function Overview() {
  const [companies, setCompanies] = useState([]);
  const [showCompanyModal, _setShowCompanyModal] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [showMenuId, setShowMenuId] = useState(null);
  const [showConfirmCompanyDeletion, setShowConfirmCompanyDeletion] =
    useState(false);
  const [companySelectedForDeletion, setCompanySelectedForDeletion] =
    useState(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const getCompanies = async () => {
    if (!showCompanyModal) {
      setIsLoadingCompanies(true);
      try {
        const { data: { companies } } = await axios.get(`${backendUrl}/companies`);
        setCompanies(companies);
      } finally {
        setIsLoadingCompanies(false);
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const fetchCompanies = async () => {
      if (!showCompanyModal) {
        setIsLoadingCompanies(true);
        try {
          const { data: { companies } } = await axios.get(`${backendUrl}/companies`);
          if (mounted) {
            setCompanies(companies);
          }
        } finally {
          if (mounted) {
            setIsLoadingCompanies(false);
          }
        }
      }
    };
    
    fetchCompanies();
    
    return () => {
      mounted = false;
    };
  }, [showCompanyModal]);
  useEffect(() => {
    if (!showCompanyModal) {
      setCompanyToEdit(null);
    }
  }, [showCompanyModal]);

  function setShowCompanyModal(state) {
    setShowMenuId(null);
    _setShowCompanyModal(state);
  }

  function confirmDeleteCompany(company) {
    // Confirmation
    setCompanySelectedForDeletion(company);
    setShowConfirmCompanyDeletion(true);
  }

  function deleteCompany(company) {
    setShowConfirmCompanyDeletion(false);
    
    // Validate and sanitize company ID to prevent SSRF attacks
    const companyId = companySelectedForDeletion?.id;
    if (!companyId || typeof companyId !== 'string' && typeof companyId !== 'number') {
      console.error('Invalid company ID');
      setCompanySelectedForDeletion(null);
      return;
    }
    
    // Encode the ID to prevent URL manipulation
    const encodedCompanyId = encodeURIComponent(String(companyId));
    
    axios
      .delete(`${backendUrl}/companies/${encodedCompanyId}`)
      .then(() => {
        setCompanySelectedForDeletion(null);
        getCompanies();
      })
      .catch((error) => {
        console.error('Failed to delete company:', error);
        setCompanySelectedForDeletion(null);
      });
  }

  function hideCompanyDeletionModal() {
    setCompanySelectedForDeletion(null);
    setShowConfirmCompanyDeletion(false);
  }

  return (
    <div onClick={() => setShowMenuId(null)}>
      <ConfirmDeleteCompanyModal
        show={showConfirmCompanyDeletion}
        hide={hideCompanyDeletionModal}
        deleteCompany={deleteCompany}
        company={companySelectedForDeletion}
      />
      <CompanyModal
        showCompanyModal={showCompanyModal}
        setShowCompanyModal={setShowCompanyModal}
        companyToEdit={companyToEdit}
        setCompanyToEdit={setCompanyToEdit}
      />
      <CompanyBox border='#ffb600'>
        <span>Add new client</span>
        <IconBtn onClick={() => setShowCompanyModal(true)} icon={AddIcon} />
      </CompanyBox>
      <BlockUi blocking={isLoadingCompanies}>
        <Container>
          {companies.map((company, index) => (
            <CompanyBox border={borderColors[index % 5]} key={company.id}>
              <StyledLink to={`/${company.id}/upload`}>{company.name}</StyledLink>
              {company.status && (
                <Status status={company.status}>{company.status}</Status>
              )}
              <IconBtn
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenuId(company.id === showMenuId ? false : company.id);
                }}
                icon={VertMenuIcon}
              />

              {showMenuId === company.id && (
                <VertMenu>
                  <VertMenuLine>
                    <IconBtn
                      onClick={(e) => {
                        setCompanyToEdit(company);
                        setShowCompanyModal(true);
                      }}
                      icon={EditIcon}
                    />
                  </VertMenuLine>
                  <VertMenuLine>
                    <IconBtn
                      onClick={(e) => {
                        confirmDeleteCompany(company);
                      }}
                      icon={DeleteIcon}
                    />
                  </VertMenuLine>
                </VertMenu>
              )}
            </CompanyBox>
          ))}
        </Container>
      </BlockUi>
    </div>
  );
}

const CompanyBox = styled(Box)`
  width: 350px;
  display: flex;
  justify-content: space-between;
  margin-right: 30px;
  position: relative;
`;

const VertMenuLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-bottom: 10px;
`;

const VertMenu = styled.div`
  position: absolute;
  top: 20px;
  right: -40px
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  z-index: 1;
  padding: 10px 10px 0px 10px;
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledLink = styled(Link)`
  color: #474747;
`;
